"use client";

import { refreshTprWorkflowProgress } from "@/configuration/groups/tpr/components/workflow-progress";
import { getInsuranceDecisionCoverage } from "@/configuration/groups/tpr/workflows/insurance-decision-coverage";
import { useInvalidateServerComponent } from "@/src/components/server/client-provider";
import { Button } from "@/src/components/shadcn/ui/button";
import { toast } from "@/src/hooks/use-toast";
import { useConfirmInsuranceNetworkMutation } from "@/src/lib/data-fetchers/tasks-fetchers";
import { receivingAssignmentOutputSchema } from "@/src/lib/workflows/steps/referral/resolve-receiving-assignment";
import type {
  AthenaCoverageReadbackOutput,
  TprInsuranceRequirementsOutput,
} from "@/src/lib/workflows/steps/step-outputs";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { TaskStatus, type Task } from "@/src/models/task";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";

export function TprNetworkStatus({
  task,
  steps,
  coverage,
  requirements,
}: {
  task: Task;
  steps: TaskStep[];
  coverage: AthenaCoverageReadbackOutput;
  requirements: TprInsuranceRequirementsOutput;
}) {
  const mutation = useConfirmInsuranceNetworkMutation();
  const invalidate = useInvalidateServerComponent("getReferralInstructions");
  const assignmentStep = steps.find(
    (step) => step.stepKey === StepKey.RESOLVE_RECEIVING_ASSIGNMENT && step.status === TaskStepStatus.COMPLETED
  );
  const parsed = receivingAssignmentOutputSchema.safeParse(assignmentStep?.output);
  const assignment = parsed.success ? parsed.data : null;
  const policy = getInsuranceDecisionCoverage(coverage);
  const snapshot = coverage.coverages.find(
    (recorded) =>
      recorded.insuranceId === policy.insuranceId && recorded.insurancePackageId === policy.insurancePackageId
  );
  const policyLabel = `${snapshot?.casePolicy ? "Case policy" : "Insurance"}: ${snapshot?.planName || "Athena package"} (package ${policy.insurancePackageId})`;

  if (policy.selfPay) return null;
  if (requirements.clinicNetwork === "in_network" && requirements.providerNetwork === "in_network")
    return (
      <div className="space-y-1 rounded-md border bg-muted/50 p-3 text-sm">
        <p className="font-medium">{policyLabel}</p>
        <p className="font-medium">
          {requirements.networkVerification
            ? "In-network — confirmed by Ops"
            : "In-network — confirmed by insurance grid"}
        </p>
        <p className="text-muted-foreground">
          Both the clinic and receiving provider accept this coverage. No additional network confirmation is needed.
          Coverage eligibility and authorization are checked separately.
        </p>
      </div>
    );

  // Gate the confirmation action; keep resolved network status visible above.
  if (
    !task.patient?.id ||
    !assignment?.providerNpi ||
    !assignment.targetDepartmentId ||
    assignment.scope === "manual_review" ||
    task.status === TaskStatus.PROCESSING
  )
    return null;

  const variantKey = requirements.networkVariantKey;

  if (variantKey === null || variantKey === undefined) {
    return (
      <div className="space-y-1 text-sm">
        <p className="font-medium">{policyLabel}</p>
        <p>Identify the insurance plan variant before confirming in-network status.</p>
      </div>
    );
  }

  const scope = {
    expectedVariantKey: variantKey,
    patientId: task.patient.id,
    insuranceId: policy.insuranceId,
    packageNumber: policy.insurancePackageId,
    providerNpi: assignment.providerNpi,
    departmentId: assignment.targetDepartmentId,
  };
  const confirm = async () => {
    try {
      await mutation.mutateAsync({ taskId: task.id, displayId: task.displayId, scope });
      refreshTprWorkflowProgress(invalidate);
      toast({ title: "In-network status confirmed" });
    } catch (error) {
      toast({
        title: "Unable to save network confirmation",
        description: error instanceof Error ? error.message : "Refresh and try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{policyLabel}</p>
      <p className="text-sm">
        After verifying that the clinic and {assignment.providerGivenName} {assignment.providerFamilyName} accept this
        patient's selected coverage, confirm below. This also applies to future documents and referrals for this patient
        while the insurance policy, provider, and clinic stay the same.
      </p>
      <Button size="sm" onClick={confirm} disabled={mutation.isPending}>
        {mutation.isPending ? "Saving..." : "Confirm in-network"}
      </Button>
    </div>
  );
}
